import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Target, 
  PiggyBank, 
  TrendingUp, 
  Calendar,
  DollarSign,
  Edit,
  Trash2,
  CheckCircle,
  Clock,
  AlertCircle,
  Sun,
  Shield,
  Home,
  Car,
  Heart,
  BookOpen,
  Plane
} from 'lucide-react';
import { BudgetingService, SavingsGoalProgress } from '../../lib/budgetingService';
import { useAuth } from '../../contexts/AuthContext';


interface SavingsGoal {
  id?: string;
  name: string;
  description: string;
  targetAmount: number;
  currentAmount: number;
  targetDate?: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  category: string;
  isAchieved: boolean;
  achievedDate?: string;
}

const goalCategories = [
  { value: 'vacation', label: 'Vacation', icon: Sun, color: 'bg-yellow-500' },
  { value: 'emergency', label: 'Emergency Fund', icon: Shield, color: 'bg-red-500' },
  { value: 'house', label: 'Home Purchase', icon: Home, color: 'bg-blue-500' },
  { value: 'car', label: 'Car Purchase', icon: Car, color: 'bg-green-500' },
  { value: 'education', label: 'Education', icon: BookOpen, color: 'bg-purple-500' },
  { value: 'retirement', label: 'Retirement', icon: TrendingUp, color: 'bg-indigo-500' },
  { value: 'wedding', label: 'Wedding', icon: Heart, color: 'bg-pink-500' },
  { value: 'travel', label: 'Travel', icon: Plane, color: 'bg-cyan-500' },
  { value: 'other', label: 'Other', icon: Target, color: 'bg-gray-500' }
];

const priorityLevels = [
  { value: 'low', label: 'Low', color: 'text-gray-600 bg-gray-100' },
  { value: 'medium', label: 'Medium', color: 'text-blue-600 bg-blue-100' },
  { value: 'high', label: 'High', color: 'text-orange-600 bg-orange-100' },
  { value: 'urgent', label: 'Urgent', color: 'text-red-600 bg-red-100' }
];

export function SavingsGoals() {
  const { user } = useAuth();
  const [goals, setGoals] = useState<SavingsGoalProgress[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingGoal, setEditingGoal] = useState<SavingsGoal | null>(null);
  const [showContributionForm, setShowContributionForm] = useState<string | null>(null);
  const [contributionAmount, setContributionAmount] = useState(0);

  const [newGoal, setNewGoal] = useState<SavingsGoal>({
    name: '',
    description: '',
    targetAmount: 0,
    currentAmount: 0,
    targetDate: '',
    priority: 'medium',
    category: 'other',
    isAchieved: false
  });

  useEffect(() => {
    loadGoals();
    
    // Listen for refresh events
    const onRefresh = () => loadGoals();
    window.addEventListener('budget:refresh', onRefresh);
    
    return () => {
      window.removeEventListener('budget:refresh', onRefresh);
    };
  }, [user?.id]);

  const loadGoals = async () => {
    if (!user?.id) return;

    try {
      setIsLoading(true);
      const data = await BudgetingService.getSavingsGoalProgress(user.id);
      setGoals(data);
    } catch (error) {
      console.error('Error loading goals:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateGoal = async () => {
    if (!user?.id || !newGoal.name || !newGoal.targetAmount) return;

    try {
      const goalData = {
        user_id: user.id,
        name: newGoal.name,
        description: newGoal.description,
        target_amount: newGoal.targetAmount,
        current_amount: newGoal.currentAmount,
        target_date: newGoal.targetDate || null,
        priority: newGoal.priority,
        category: newGoal.category,
        is_achieved: newGoal.isAchieved,
        achieved_date: newGoal.achievedDate || null
      };

      const created = await BudgetingService.createSavingsGoal(goalData);
      await loadGoals(); // Reload to get updated progress
      setShowForm(false);
      resetForm();
    } catch (error) {
      console.error('Error creating goal:', error);
    }
  };

  const handleUpdateGoal = async (id: string, updates: Partial<SavingsGoal>) => {
    try {
      await BudgetingService.updateSavingsGoal(id, {
        name: updates.name,
        description: updates.description,
        target_amount: updates.targetAmount,
        current_amount: updates.currentAmount,
        target_date: updates.targetDate,
        priority: updates.priority,
        category: updates.category,
        is_achieved: updates.isAchieved,
        achieved_date: updates.achievedDate
      });

      await loadGoals(); // Reload to get updated progress
      setEditingGoal(null);
    } catch (error) {
      console.error('Error updating goal:', error);
    }
  };

  const handleDeleteGoal = async (id: string) => {
    if (!confirm('Are you sure you want to abandon this savings goal? This action cannot be undone.')) {
      return;
    }

    try {
      await BudgetingService.deleteSavingsGoal(id);
      setGoals(goals.filter(goal => goal.goal.id !== id));
      window.dispatchEvent(new CustomEvent('budget:refresh'));
      window.dispatchEvent(new CustomEvent('budget:toast', { 
        detail: { type: 'success', message: 'Savings goal deleted successfully' } 
      }));
    } catch (error) {
      console.error('Error deleting goal:', error);
      window.dispatchEvent(new CustomEvent('budget:toast', { 
        detail: { type: 'error', message: 'Failed to delete savings goal' } 
      }));
    }
  };

  const handleAddContribution = async (goalId: string) => {
    if (!contributionAmount || contributionAmount <= 0 || !user?.id) return;

    try {
      // Get the goal to get its name
      const goal = goals.find(g => g.goal.id === goalId);
      if (!goal) return;

      // Create savings contribution
      await BudgetingService.createSavingsContribution({
        savings_goal_id: goalId,
        amount: contributionAmount,
        contribution_date: new Date().toISOString().split('T')[0],
        description: `Contribution to ${goal.goal.name}`,
        source: 'manual'
      });

      // Create a transfer transaction for the savings contribution
      const activeBudget = await BudgetingService.getActiveBudget(user.id);
      if (!activeBudget) {
        // Create a default budget if none exists
        await BudgetingService.createBudget({
          user_id: user.id,
          name: 'Default Budget',
          description: 'Auto-created budget',
          period_type: 'monthly',
          start_date: new Date().toISOString().split('T')[0],
          total_income: 0,
          total_expenses: 0,
          total_savings: 0,
          is_active: true
        } as any);
      }

      const budget = activeBudget || await BudgetingService.getActiveBudget(user.id);
      if (budget) {
        // Get or create a savings category
        const categories = await BudgetingService.getBudgetCategories(user.id);
        let savingsCategory = categories.find(c => c.type === 'savings' && c.name.toLowerCase().includes('savings'));
        
        if (!savingsCategory) {
          savingsCategory = await BudgetingService.createBudgetCategory({
            user_id: user.id,
            name: 'Savings',
            type: 'savings',
            color: '#10b981',
            icon: 'piggy-bank'
          } as any);
        }

        // Create transfer transaction
        await BudgetingService.createBudgetTransaction({
          user_id: user.id,
          budget_id: budget.id,
          category_id: savingsCategory.id,
          amount: contributionAmount,
          type: 'transfer' as any,
          description: `Savings transfer: ${goal.goal.name}`,
          transaction_date: new Date().toISOString().split('T')[0],
          payment_method: 'bank_transfer',
          tags: ['savings', 'goal-contribution'],
          is_recurring: false,
          recurring_frequency: null
        } as any);
      }

      await loadGoals(); // Reload to get updated progress
      setShowContributionForm(null);
      setContributionAmount(0);
      
      // Trigger refresh for other components
      window.dispatchEvent(new CustomEvent('budget:refresh'));
      window.dispatchEvent(new CustomEvent('budget:toast', { 
        detail: { type: 'success', message: `Added $${contributionAmount.toFixed(2)} to ${goal.goal.name}` } 
      }));
    } catch (error) {
      console.error('Error adding contribution:', error);
      window.dispatchEvent(new CustomEvent('budget:toast', { 
        detail: { type: 'error', message: 'Failed to add contribution' } 
      }));
    }
  };

  const resetForm = () => {
    setNewGoal({
      name: '',
      description: '',
      targetAmount: 0,
      currentAmount: 0,
      targetDate: '',
      priority: 'medium',
      category: 'other',
      isAchieved: false
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(amount);
  };

  const getCategoryIcon = (category: string) => {
    const categoryConfig = goalCategories.find(cat => cat.value === category);
    return categoryConfig ? categoryConfig.icon : Target;
  };

  const getCategoryColor = (category: string) => {
    const categoryConfig = goalCategories.find(cat => cat.value === category);
    return categoryConfig ? categoryConfig.color : 'bg-gray-500';
  };

  const getPriorityColor = (priority: string) => {
    const priorityConfig = priorityLevels.find(p => p.value === priority);
    return priorityConfig ? priorityConfig.color : 'text-gray-600 bg-gray-100';
  };

  const getProgressColor = (progress: number) => {
    if (progress >= 100) return 'bg-green-500';
    if (progress >= 75) return 'bg-blue-500';
    if (progress >= 50) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getDaysRemaining = (targetDate?: string) => {
    if (!targetDate) return null;
    const days = Math.ceil((new Date(targetDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
    return days > 0 ? days : 0;
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <div className="h-7 bg-gray-200 rounded w-40 mb-2 animate-pulse" />
            <div className="h-4 bg-gray-200 rounded w-56 animate-pulse" />
          </div>
        </div>
        <div className="space-y-6">
          {[1, 2].map(i => (
            <div key={i} className="bg-white border rounded-lg p-6 animate-pulse">
              <div className="flex items-start space-x-4 mb-4">
                <div className="w-12 h-12 bg-gray-200 rounded-lg" />
                <div className="flex-1">
                  <div className="h-5 bg-gray-200 rounded w-40 mb-2" />
                  <div className="h-3 bg-gray-200 rounded w-64" />
                </div>
              </div>
              <div className="h-3 bg-gray-200 rounded-full w-full mb-4" />
              <div className="h-4 bg-gray-200 rounded w-24" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Savings Goals</h2>
          <p className="text-gray-600">Set and track your financial goals</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" />
          <span>New Goal</span>
        </button>
      </div>

      {/* New/Edit Goal Form */}
      {(showForm || editingGoal) && (
        <div className="bg-white border rounded-lg p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {editingGoal ? 'Edit Goal' : 'Create New Goal'}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Goal Name
              </label>
              <input
                type="text"
                value={editingGoal?.name || newGoal.name}
                onChange={(e) => {
                  if (editingGoal) {
                    setEditingGoal({ ...editingGoal, name: e.target.value });
                  } else {
                    setNewGoal({ ...newGoal, name: e.target.value });
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g., Emergency Fund"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              <select
                value={editingGoal?.category || newGoal.category}
                onChange={(e) => {
                  if (editingGoal) {
                    setEditingGoal({ ...editingGoal, category: e.target.value });
                  } else {
                    setNewGoal({ ...newGoal, category: e.target.value });
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {goalCategories.map(category => (
                  <option key={category.value} value={category.value}>
                    {category.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Target Amount
              </label>
              <input
                type="number"
                value={editingGoal?.targetAmount || newGoal.targetAmount}
                onChange={(e) => {
                  if (editingGoal) {
                    setEditingGoal({ ...editingGoal, targetAmount: Number(e.target.value) });
                  } else {
                    setNewGoal({ ...newGoal, targetAmount: Number(e.target.value) });
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Current Amount
              </label>
              <input
                type="number"
                value={editingGoal?.currentAmount || newGoal.currentAmount}
                onChange={(e) => {
                  if (editingGoal) {
                    setEditingGoal({ ...editingGoal, currentAmount: Number(e.target.value) });
                  } else {
                    setNewGoal({ ...newGoal, currentAmount: Number(e.target.value) });
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Target Date
              </label>
              <input
                type="date"
                value={editingGoal?.targetDate || newGoal.targetDate}
                onChange={(e) => {
                  if (editingGoal) {
                    setEditingGoal({ ...editingGoal, targetDate: e.target.value });
                  } else {
                    setNewGoal({ ...newGoal, targetDate: e.target.value });
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Priority
              </label>
              <select
                value={editingGoal?.priority || newGoal.priority}
                onChange={(e) => {
                  if (editingGoal) {
                    setEditingGoal({ ...editingGoal, priority: e.target.value as any });
                  } else {
                    setNewGoal({ ...newGoal, priority: e.target.value as any });
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {priorityLevels.map(priority => (
                  <option key={priority.value} value={priority.value}>
                    {priority.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={editingGoal?.description || newGoal.description}
                onChange={(e) => {
                  if (editingGoal) {
                    setEditingGoal({ ...editingGoal, description: e.target.value });
                  } else {
                    setNewGoal({ ...newGoal, description: e.target.value });
                  }
                }}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Describe your goal..."
              />
            </div>
          </div>
          <div className="flex justify-end space-x-3 mt-4">
            <button
              onClick={() => {
                setShowForm(false);
                setEditingGoal(null);
                resetForm();
              }}
              className="px-4 py-2 text-gray-600 hover:text-gray-900"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                if (editingGoal) {
                  handleUpdateGoal(editingGoal.id!, editingGoal);
                } else {
                  handleCreateGoal();
                }
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              {editingGoal ? 'Update' : 'Create'} Goal
            </button>
          </div>
        </div>
      )}

      {/* Goals List */}
      <div className="space-y-6">
        {goals.length > 0 ? (
          goals.map((goalProgress) => {
            const CategoryIcon = getCategoryIcon(goalProgress.goal.category);
            const categoryColor = getCategoryColor(goalProgress.goal.category);
            const priorityColor = getPriorityColor(goalProgress.goal.priority);
            const progressColor = getProgressColor(goalProgress.progress);
            const daysRemaining = getDaysRemaining(goalProgress.goal.target_date);

            return (
              <div key={goalProgress.goal.id} className="bg-white border rounded-lg p-6 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start space-x-4">
                    <div className={`p-3 rounded-lg ${categoryColor} text-white`}>
                      <CategoryIcon className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-xl font-semibold text-gray-900">{goalProgress.goal.name}</h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${priorityColor}`}>
                          {goalProgress.goal.priority}
                        </span>
                        {goalProgress.goal.is_achieved && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium text-green-600 bg-green-100">
                            <CheckCircle className="w-3 h-3 inline mr-1" />
                            Achieved
                          </span>
                        )}
                      </div>
                      {goalProgress.goal.description && (
                        <p className="text-gray-600 mb-3">{goalProgress.goal.description}</p>
                      )}
                      <div className="flex items-center space-x-6 text-sm text-gray-500">
                        <div className="flex items-center space-x-1">
                          <DollarSign className="w-4 h-4" />
                          <span>{formatCurrency(goalProgress.goal.current_amount)} / {formatCurrency(goalProgress.goal.target_amount)}</span>
                        </div>
                        {goalProgress.goal.target_date && (
                          <div className="flex items-center space-x-1">
                            <Calendar className="w-4 h-4" />
                            <span>Target: {new Date(goalProgress.goal.target_date).toLocaleDateString()}</span>
                          </div>
                        )}
                        {daysRemaining !== null && daysRemaining > 0 && (
                          <div className="flex items-center space-x-1">
                            <Clock className="w-4 h-4" />
                            <span>{daysRemaining} days remaining</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setEditingGoal(goalProgress.goal)}
                      className="p-2 text-gray-400 hover:text-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
                      title="Edit goal"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => goalProgress.goal.id && handleDeleteGoal(goalProgress.goal.id)}
                      className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                      title="Abandon goal"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700">Progress</span>
                    <span className="text-sm font-medium text-gray-700">{goalProgress.progress.toFixed(1)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div 
                      className={`h-3 rounded-full transition-all duration-300 ${progressColor}`}
                      style={{ width: `${Math.min(goalProgress.progress, 100)}%` }}
                    />
                  </div>
                </div>

                {/* Monthly Target */}
                {goalProgress.monthlyTarget > 0 && !goalProgress.goal.is_achieved && (
                  <div className="bg-blue-50 rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-blue-900">Monthly Target</p>
                        <p className="text-lg font-semibold text-blue-700">
                          {formatCurrency(goalProgress.monthlyTarget)}
                        </p>
                      </div>
                      <PiggyBank className="w-6 h-6 text-blue-600" />
                    </div>
                  </div>
                )}

                {/* Add Contribution */}
                {!goalProgress.goal.is_achieved && (
                  <div className="flex items-center space-x-4">
                    {showContributionForm === goalProgress.goal.id ? (
                      <div className="flex items-center space-x-2">
                        <input
                          type="number"
                          value={contributionAmount}
                          onChange={(e) => setContributionAmount(Number(e.target.value))}
                          placeholder="Amount"
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        <button
                          onClick={() => handleAddContribution(goalProgress.goal.id!)}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                        >
                          Add
                        </button>
                        <button
                          onClick={() => {
                            setShowContributionForm(null);
                            setContributionAmount(0);
                          }}
                          className="px-4 py-2 text-gray-600 hover:text-gray-900"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setShowContributionForm(goalProgress.goal.id!)}
                        className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                      >
                        <Plus className="w-4 h-4" />
                        <span>Add Contribution</span>
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })
        ) : (
          <div className="text-center py-12">
            <Target className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Savings Goals</h3>
            <p className="text-gray-500 mb-6">Start planning your financial future by setting savings goals</p>
            <button
              onClick={() => setShowForm(true)}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Create Your First Goal
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
